import { useTranslation } from 'react-i18next';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';

import { useAuth } from 'providers/AuthProvider'
import apiClient, { handleAPIError } from 'helpers/apiService';

import { TrendPoint } from './components/DataDefinitions';
import { Card, Tab, Tabs } from 'react-bootstrap';
import WorkLifeBalanceTrend from './components/WorkLifeBalanceTrend';
import WorkLifeBalancePie from './components/WorkLifeBalancePie';
import { useEffect, useState } from 'react';

const WorkLifeBalanceTracker = () => {

    const { t } = useTranslation();
    const auth = useAuth();
    const [loading, setLoading] = useState(true);

    const [weeklyData, setWeeklyData] = useState<TrendPoint[]>();
    const [monthlyData, setMonthlyData] = useState<TrendPoint[]>();
    const [yearlyData, setYearlyData] = useState<TrendPoint[]>();

    const breadcrumbItems: PageBreadcrumbItem[] = [
        {
            label: t('Dashboard'),
            url: '/dashboard/home'
        },
        {
            label: t('Analytics.page_title'),
            url: '/analytics'
        },
        {
            label: t('WorkLifeBalanceTracker.page_title'),
            active: true
        }
    ];

    const balanceAdvice = {
        immediate: [
            {
                title: "🎯 Set Clear Boundaries",
                description: "Establish specific work hours and stick to them. Create a dedicated workspace separate from personal areas.",
                actionItems: [
                    "Define work start and end times",
                    "Turn off work notifications after hours",
                    "Create a physical boundary between work and personal space"
                ]
            },
            {
                title: "⏰ Time Blocking",
                description: "Dedicate specific time slots for both work and personal activities.",
                actionItems: [
                    "Schedule personal activities first",
                    "Block focused work time",
                    "Include buffer time between activities"
                ]
            },
            {
                title: "🧘‍♂️ Daily Self-Care",
                description: "Prioritize activities that maintain your well-being.",
                actionItems: [
                    "30 minutes daily exercise",
                    "Regular meal breaks",
                    "Mindfulness or meditation practice"
                ]
            }
        ],
        longTerm: [
            {
                title: "📈 Career Development",
                description: "Focus on efficiency and growth in your professional life.",
                actionItems: [
                    "Learn productivity techniques",
                    "Develop new skills",
                    "Network strategically"
                ]
            },
            {
                title: "❤️ Personal Growth",
                description: "Invest in personal relationships and hobbies.",
                actionItems: [
                    "Schedule regular family time",
                    "Pursue a hobby weekly",
                    "Plan social activities"
                ]
            },
            {
                title: "🎯 Goal Setting",
                description: "Create balanced goals for both areas of life.",
                actionItems: [
                    "Set quarterly personal goals",
                    "Establish career milestones",
                    "Regular progress review"
                ]
            }
        ]
    };

    const fetchWeeklyData = async () => {
        try {
            const response = await apiClient.get('/v1/analytics/wl-trend?year=2024');
            setWeeklyData(response.data.weeklyData);
            setMonthlyData(response.data.monthlyData);
            setYearlyData(response.data.yearlyData);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchWeeklyData();
    }, []);



    if (loading || weeklyData == null || monthlyData == null || yearlyData == null) {
        return <div>Loading...</div>;
    }


    return (
        <>
            <PageBreadcrumb items={breadcrumbItems} />
            <div className="mt-3">
                <WorkLifeBalanceTrend weeklyData={weeklyData} monthlyData={monthlyData} yearlyData={yearlyData} />
            </div>

            <div className="mt-3">
                <WorkLifeBalancePie />
            </div>


            <div className="mt-3">



                <Card className="mb-3">
                    <Card.Header className='pb-0 border-bottom-0'>
                        <Card.Title className="fs-6 fw-bold">Balance Improvement Strategies</Card.Title>
                        <Card.Subtitle>
                            Comprehensive advice to help you achieve a better work-life balance
                        </Card.Subtitle>
                    </Card.Header>
                    <Card.Body>
                        <Tabs defaultActiveKey="immediate" className="nav-underline mb-2">
                            <Tab eventKey="immediate" title="Immediate Actions">

                                {balanceAdvice.immediate.map((advice, index) => (
                                    <Card key={index} className='mb-4'>
                                        <Card.Header className='pb-0 border-bottom-0'>
                                            <Card.Title>{advice.title}</Card.Title>
                                            <Card.Text>{advice.description}</Card.Text>
                                        </Card.Header>
                                        <Card.Body>
                                            <ul className="list-disc">
                                                {advice.actionItems.map((item, i) => (
                                                    <li key={i} className="mb-2">{item}</li>
                                                ))}
                                            </ul>
                                        </Card.Body>
                                    </Card>
                                ))}

                            </Tab>
                            <Tab eventKey="longterm" title="Long-term Strategies">

                                {balanceAdvice.longTerm.map((advice, index) => (
                                    <Card key={index} className='mb-4'>
                                        <Card.Header className='pb-0 border-bottom-0'>
                                            <Card.Title>{advice.title}</Card.Title>
                                            <Card.Text>{advice.description}</Card.Text>
                                        </Card.Header>
                                        <Card.Body>
                                            <ul className="list-disc">
                                                {advice.actionItems.map((item, i) => (
                                                    <li key={i} className="mb-2">{item}</li>
                                                ))}
                                            </ul>
                                        </Card.Body>
                                    </Card>
                                ))}

                            </Tab>
                        </Tabs>
                    </Card.Body>
                </Card>
            </div>
        </>
    );
};

export default WorkLifeBalanceTracker;